import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { Store, select } from '@ngrx/store'
import { Component, OnInit, OnDestroy, Input } from '@angular/core'
import FormTrait from 'src/app/traits/form.trait'
import { Subscription } from 'rxjs'
import { getUpdateBusinessState } from '@containers/businesses/businesses.selectors'
import {
  CreateBusinessPaymentRequest,
  DeleteBusinessPaymentRequest,
  DownloadBusinessPaymentRequest,
  FetchBusinessPaymentsRequest,
  UpdateBusinessPaymentRequest,
  SetDefaultData,
} from '@containers/businesses/businesses.actions'
import { getUnitsSettingsState } from '@containers/units-settings/units-settings.selectors'
import { getSettingsState } from '@containers/settings/settings.selectors'
import { FormBuilder, FormGroup } from '@angular/forms'
import { SearchPaymentPurposesRequest } from '@shared/shared.actions'
import { getSearchPaymentPurposesState } from '@shared/shared.select'
import Validator from '@shared/validatorjs/src/validator'
import * as moment from 'moment'

enum DATA_CYCLE_FILTER {
  Current,
  All,
}

@Component({
  selector: 'payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.css'],
})
export class PaymentsComponent extends FormTrait implements OnInit, OnDestroy {
  @Input() isReadonly: boolean = false
  public businessId: number
  public payments: any[]
  private state$: Subscription
  private settings$: Subscription
  public isLoading: boolean
  public currency: string
  public isPaymentFormOpen = false
  public isPaymentCreateLoading = false
  public isPaymentUpdateLoading = false
  public isPaymentDownloading = false
  public paymentPurposes = []
  public validationErrors: any = {}
  public expandedId: any
  form: FormGroup
  private searchPaymentPurposesState$: Subscription

  public radioOptions = [
    {
      label: 'CURRENT_ONLY',
      value: DATA_CYCLE_FILTER.Current,
    },
    {
      label: 'ALL_RECORDS',
      value: DATA_CYCLE_FILTER.All,
    },
  ]

  public dataCycle: DATA_CYCLE_FILTER = DATA_CYCLE_FILTER.Current
  public currentDataCycleId: any

  constructor(
    public store: Store<any>,
    private route: ActivatedRoute,
    private fb: FormBuilder,
  ) {
    super()

    this.form = this.fb.group({
      amount: [''],
      payableId: [''],
      id: [''],
      action: [''],
      paymentPurposeId: [''],
      transactionId: [''],
      paymentDate: [''],
      notes: [''],
    })
  }

  ngOnInit() {
    this.store.dispatch(new SetDefaultData('update.payments'))
    this.businessId = parseInt(this.route.snapshot.paramMap.get('id'))

    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        this.currentDataCycleId = state.getIn([
          'data',
          'system',
          'SYSTEM_ACTIVE_DATA_CYCLE',
          'id',
        ])

        this.currency = state.getIn([
          'data',
          'units',
          'SETTINGS_UNITS_CURRENCY_SYMBOL',
        ])
      })

    this.state$ = this.store
      .pipe(select(getUpdateBusinessState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.getIn(['payments', 'isLoading'])
        this.payments = this.payments = this.filterPayments(
          state.getIn(['payments', 'data']),
        ).toJS()
        this.isPaymentDownloading = state.getIn([
          'downloadPayment',
          'isLoading',
        ])

        if (
          (this.isPaymentCreateLoading &&
            !state.getIn(['createPayment', 'isLoading'])) ||
          (this.isPaymentUpdateLoading &&
            !state.getIn(['updatePayment', 'isLoading']))
        ) {
          this.isPaymentFormOpen = false
        }

        this.isPaymentCreateLoading = state.getIn([
          'createPayment',
          'isLoading',
        ])

        this.isPaymentUpdateLoading = state.getIn([
          'updatePayment',
          'isLoading',
        ])
      })

    this.searchPaymentPurposesState$ = this.store
      .pipe(select(getSearchPaymentPurposesState))
      .subscribe((state) => {
        this.paymentPurposes = state.get('data').toJS()
      })

    this.store.dispatch<any>(new SearchPaymentPurposesRequest(''))
    this.store.dispatch<any>(new FetchBusinessPaymentsRequest(this.businessId))
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.settings$.unsubscribe()
    this.searchPaymentPurposesState$.unsubscribe()
  }

  public isCurrentCycle(payment: any) {
    return this.currentDataCycleId === payment.dataCycle.id
  }

  handleCreatePayment(payable: any) {
    this.form.reset()
    this.form.get('action').setValue('create')
    this.form.get('payableId').setValue(payable.id)

    this.isPaymentFormOpen = true
  }

  handleUpdatePayment({ paymentDate, ...payment }: any) {
    this.form.reset()
    this.form.get('action').setValue('update')
    this.form.patchValue({
      paymentDate: moment(paymentDate, 'DD-MM-yyyy').format('yyyy-MM-DD'),
      payableId: payment.payable.id,
      ...payment,
    })

    this.isPaymentFormOpen = true
  }

  handleDeletePayment(payment: any) {
    this.store.dispatch(new DeleteBusinessPaymentRequest(payment))
  }

  handleDownloadPayment(payment: any) {
    this.store.dispatch(new DownloadBusinessPaymentRequest(payment))
  }

  validation() {
    const data = this.form.value
    const rules = {
      amount: 'numeric|required',
      paymentDate: 'required',
      // transactionId: 'required'
    }
    const validator = new Validator(data, rules)
    validator.setAttributeNames({
      transactionId: 'bank Id',
      paymentDate: 'payment date',
    })
    return validator
  }

  getPaymentError(field: string) {
    return this.validationErrors[field]
  }

  hasPaymentError(field: string) {
    return !!this.validationErrors[field]
  }

  public dataCycleChange(value) {
    this.dataCycle = value

    this.payments = this.filterPayments(
      this.state.getIn(['payments', 'data']),
    ).toJS()
  }

  public filterPayments(payments: any) {
    return payments.filter((payment) =>
      this.dataCycle === DATA_CYCLE_FILTER.Current
        ? payment.getIn(['dataCycle', 'id']) === this.currentDataCycleId
        : true,
    )
  }

  onPaymentSubmit() {
    const validation = this.validation()
    const { paymentDate, ...payload } = this.form.value

    if (validation.passes()) {
      this.validationErrors = {}
      if (payload.action === 'create') {
        this.store.dispatch(
          new CreateBusinessPaymentRequest({
            paymentDate: moment(paymentDate, 'yyyy-MM-DD').format('DD-MM-yyyy'),
            ...payload,
          }),
        )
      } else if (payload.action === 'update') {
        this.store.dispatch(
          new UpdateBusinessPaymentRequest({
            paymentDate: moment(paymentDate, 'yyyy-MM-DD').format('DD-MM-yyyy'),
            ...payload,
          }),
        )
      }
    } else {
      this.validationErrors = validation.errors.errors
    }
  }
}
